import { AESituacion, HaciendaApi, HaciendaNotRegisteredError } from "@/atv/HaciendaApi";
import {
    TIPO_DE_IDENTIFICACION_DIMEX,
    TIPO_DE_IDENTIFICACION_FISICA,
    TIPO_DE_IDENTIFICACION_JURIDICA,
} from "@utils/Constants";
import { normalizeIdentification, validateIdentification } from "@utils/Identification";

/**
 * Que sabe Hacienda de una identificacion.
 *
 * - `registered`: esta inscrita ante Tributacion y se puede usar como tercero.
 * - `not_registered`: respuesta definitiva de que no lo esta. Hacienda rechazaria el
 *   comprobante con el codigo -105, asi que este es el unico estado que bloquea.
 * - `unknown`: no se pudo averiguar (sin conexion, tipo de identificacion que el padron no
 *   responde, o numero mal formado). Nunca bloquea.
 */
export type TaxpayerStatus = "registered" | "not_registered" | "unknown";

export type TaxpayerLookup = {
    status: TaxpayerStatus;
    nombre?: string;
    tipoIdentificacion?: string;
    /** Codigo de la actividad economica principal, si la tiene. */
    activityCode?: string;
    moroso?: boolean;
    omiso?: boolean;
};

/**
 * Tipos de identificacion que el padron de Hacienda puede responder.
 *
 * Quedan fuera el NITE ("04"), el Extranjero No Domiciliado ("05") y el No Contribuyente
 * ("06"): son justamente los tipos de quien no esta inscrito, asi que exigirles inscripcion
 * bloquearia cobros de tercero legitimos.
 */
const PADRON_VERIFIABLE_ID_TYPES = [
    TIPO_DE_IDENTIFICACION_FISICA,
    TIPO_DE_IDENTIFICACION_JURIDICA,
    TIPO_DE_IDENTIFICACION_DIMEX,
];

export const isPadronVerifiableIdType = (tipo?: string | null): boolean =>
    PADRON_VERIFIABLE_ID_TYPES.includes(`${tipo ?? ""}`);

/** Estados con que Hacienda marca a un contribuyente inscrito. */
const REGISTERED_STATES = ["inscrito", "inscrito de oficio"];

export const isTaxpayerRegistered = (situacion?: AESituacion | null): boolean =>
    REGISTERED_STATES.includes(`${situacion?.estado ?? ""}`.trim().toLowerCase());

/**
 * Cache de proceso. Solo guarda respuestas definitivas: un `unknown` viene de que Hacienda no
 * contesto, y cachearlo condenaria la sesion entera a no poder verificar nada.
 */
const registryCache = new Map<string, TaxpayerLookup>();

const cacheKey = (tipo: string, numero: string): string => `${tipo}:${numero}`;

const UNKNOWN: TaxpayerLookup = { status: "unknown" };

/**
 * Consulta el padron de Tributacion. Nunca lanza.
 *
 * La cache es lo que hace viable llamarla desde un `.test` de Yup, que Formik corre en cada
 * cambio del formulario: cada identificacion distinta sale a la red una sola vez por sesion.
 */
export const resolveTaxpayer = async (
    tipo?: string | null,
    numero?: string | null
): Promise<TaxpayerLookup> => {
    const identificationType = `${tipo ?? ""}`;
    const identification = normalizeIdentification(numero);

    // De la forma ya se encarga `validateIdentification`; ademas evita consultar a Hacienda
    // con un numero que el usuario todavia esta escribiendo.
    if (!isPadronVerifiableIdType(identificationType)) return UNKNOWN;
    if (!validateIdentification(identificationType, identification).valid) return UNKNOWN;

    const key = cacheKey(identificationType, identification);
    const cached = registryCache.get(key);
    if (cached) return cached;

    try {
        const info = await HaciendaApi.getActividadesEconomicas(identification);
        const principal = info?.actividades?.find((actividad) => actividad.tipo === "P");
        const result: TaxpayerLookup = {
            status: isTaxpayerRegistered(info?.situacion) ? "registered" : "not_registered",
            nombre: info?.nombre,
            tipoIdentificacion: info?.tipoIdentificacion,
            activityCode: principal ? `${principal.codigo}` : undefined,
            moroso: info?.situacion?.moroso === "SI",
            omiso: info?.situacion?.omiso === "SI",
        };
        registryCache.set(key, result);
        return result;
    } catch (error) {
        if (error instanceof HaciendaNotRegisteredError) {
            const result: TaxpayerLookup = { status: "not_registered" };
            registryCache.set(key, result);
            return result;
        }
        // Conexion caida o respuesta inesperada: no se sabe, y no se cachea.
        console.warn(`No fue posible verificar la identificacion ${identification} en Hacienda`, error);
        return UNKNOWN;
    }
};

/** Mensaje unico del bloqueo, reusado por los formularios y por el costo de envio. */
export const TAXPAYER_NOT_REGISTERED_MESSAGE =
    "Esta identificación no está inscrita ante Tributación. Hacienda rechaza el comprobante si se cobra a nombre de un tercero no inscrito.";

/** Aviso no bloqueante de que la verificacion no se pudo hacer. */
export const TAXPAYER_UNVERIFIED_MESSAGE =
    "No se pudo verificar la identificación con Hacienda. Revísela antes de facturar.";

/** Solo para los tests: limpia la cache de proceso. */
export const clearTaxpayerRegistryCache = (): void => {
    registryCache.clear();
};
